import React from "react";
import { Toast, OverlayToaster, Intent, IconName } from "@blueprintjs/core";
import { observer } from "mobx-react";
import { NotificationType } from "modules/common/services/notifications/types";
import { InlineToasterOptions } from "./toaster";
import { useMst } from "modules/root/models/hooks";

export const Notifications: React.FC = observer(() => {
  const { notifications } = useMst();

  return (
    <OverlayToaster {...InlineToasterOptions}>
      {notifications.alerts.map((alert) => {
        const { id, type, message, link, context, title, details, linkText } = alert;
        return (
          <Toast
            icon={icon(type)}
            key={id}
            onDismiss={() => notifications.closeNotification(id)}
            message={
              <ToastContent
                message={message}
                context={context}
                title={title}
                details={details}
                link={link}
                linkText={linkText}
                onOpen={() => notifications.closeNotification(id)}
              />
            }
            intent={intent(type)}
            timeout={timeout(type)}
          />
        );
      })}
    </OverlayToaster>
  );
});

/**
 * Сервер присылает полную ссылку с доменом из своих настроек. Открываем путь на текущем сайте:
 * домен в настройках может не совпадать с адресом, по которому открыто приложение
 */
const localPath = (link: string) => {
  try {
    const url = new URL(link, window.location.origin);
    return url.pathname + url.search + url.hash;
  } catch {
    return link;
  }
};

/**
 * Содержимое уведомления. Если сервер прислал части — показываем их раздельно: о чём оно,
 * про что именно и что произошло. Иначе — текст целиком. Ссылка — кнопкой под текстом
 */
const ToastContent: React.FC<ToastContentProps> = ({ message, context, title, details, link, linkText, onOpen }) => (
  <>
    {title ? (
      <>
        {context && <div className="toast-context">{context}</div>}
        <div className="toast-title">{title}</div>
        {details.length > 0 && (
          <ul className="toast-details">
            {details.map((line, index) => (
              <li key={index}>{line}</li>
            ))}
          </ul>
        )}
      </>
    ) : (
      message
    )}
    {link && (
      <div>
        <a
          className="toast-open-link"
          href={localPath(link)}
          target="_blank"
          rel="noopener noreferrer"
          // тост закрываем после перехода: если убрать ссылку прямо в обработчике,
          // браузер может не успеть открыть вкладку
          onClick={() => setTimeout(onOpen)}
        >
          {linkText || "Открыть"}
        </a>
      </div>
    )}
  </>
);

interface ToastContentProps {
  message: string;
  context: string;
  title: string;
  details: readonly string[];
  link: string;
  linkText: string;
  onOpen: () => void;
}

export const icon = (type: NotificationType): IconName => {
  switch (type) {
    case "error":
      return "disable";
    case "success":
      return "small-tick";
    case "warning":
      return "notifications";
    default:
      return "error";
  }
};

export const timeout = (type: NotificationType) => (type === "error" || type === "warning" ? 0 : 5000);

export const intent = (type: NotificationType): Intent => {
  switch (type) {
    case "error":
      return "danger";
    case "success":
      return "success";
    case "warning":
      return "primary";
    default:
      return "none";
  }
};
